import { Link, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  OtpInput,
  PhoneInput,
  Stepper,
  Stack,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FseApiError } from '../api/client';
import {
  authControllerRequestOtp,
  authControllerVerifyOtp,
  authControllerResetPin,
} from '../api/generated/auth/auth';

/**
 * Public "Forgot PIN" flow (coop_admin self-service re-credential).
 *
 * Three steps, mirroring the mobile reset-pin screens:
 *   1. enter phone        -> POST /auth/otp/request {purpose:'pin_reset'}
 *   2. enter 5-digit OTP  -> POST /auth/otp/verify  {purpose:'pin_reset'}
 *   3. enter new PIN x2   -> POST /auth/reset-pin    -> AuthTokens
 *
 * On the final success the server mints tokens (same shape login returns); we
 * persist them exactly like login.tsx and navigate to /dashboard.
 *
 * Copy is i18n via react-i18next (TD-072); keys live under the `forgotPin.*`
 * namespace in the locale JSON.
 */

// Phone is captured as the 9-digit ZA-local portion (PhoneInput renders the
// fixed `+27` adornment). We rebuild the E.164 string at the API boundary, so
// the request payload is unchanged. Validation messages are resolved through
// t() inside the component (TD-072), so these schemas validate shape only.
const phoneSchema = z.object({
  phone: z.string().regex(/^\d{9}$/),
});

const otpSchema = z.object({
  code: z.string().regex(/^\d{5}$/),
});

const pinSchema = z
  .object({
    pin: z.string().regex(/^\d{4,8}$/),
    confirmPin: z.string(),
  })
  .refine((v) => v.pin === v.confirmPin, {
    path: ['confirmPin'],
  });

function persistTokens(tokens: { access_token: string; refresh_token: string }) {
  window.localStorage.setItem('fse_access_token', tokens.access_token);
  window.localStorage.setItem('fse_refresh_token', tokens.refresh_token);
}

export function ForgotPinScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const steps = [t('forgotPin.step.phone'), t('forgotPin.step.verify'), t('forgotPin.step.newPin')];

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{
    phone?: string;
    code?: string;
    pin?: string;
    confirmPin?: string;
  }>({});
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function mapApiError(err: unknown, kind: 'request' | 'verify' | 'reset'): string {
    if (err instanceof FseApiError) {
      if (err.status === 404) {
        return t('forgotPin.error.notFound');
      }
      if (err.status === 401) {
        return kind === 'reset'
          ? t('forgotPin.error.expired')
          : t('forgotPin.error.badCode');
      }
      if (err.status === 400) {
        return t('forgotPin.error.badRequest');
      }
      return err.message;
    }
    return String(err);
  }

  async function onSubmitPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = phoneSchema.safeParse({ phone });
    if (!parsed.success) {
      setFieldErrors({ phone: t('forgotPin.error.phoneInvalid') });
      return;
    }
    setPending(true);
    try {
      // Generated orval client (TD-070) + E.164 rebuilt from the 9 local digits (TD-089).
      await authControllerRequestOtp({ phone: `+27${parsed.data.phone}`, purpose: 'pin_reset' });
      setStep(1);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'request'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = otpSchema.safeParse({ code });
    if (!parsed.success) {
      setFieldErrors({ code: t('forgotPin.error.codeInvalid') });
      return;
    }
    setPending(true);
    try {
      await authControllerVerifyOtp({ phone: `+27${phone}`, code: parsed.data.code, purpose: 'pin_reset' });
      setStep(2);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'verify'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitPin(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = pinSchema.safeParse({ pin, confirmPin });
    if (!parsed.success) {
      const errs: { pin?: string; confirmPin?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        // `pin` only fails the regex; `confirmPin` only fails the equality refine.
        if (key === 'pin') errs.pin = t('forgotPin.error.pinInvalid');
        if (key === 'confirmPin') errs.confirmPin = t('forgotPin.error.pinMismatch');
      }
      setFieldErrors(errs);
      return;
    }
    setPending(true);
    try {
      const tokens = await authControllerResetPin({ phone: `+27${phone}`, pin: parsed.data.pin });
      persistTokens(tokens);
      void navigate({ to: '/dashboard' });
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'reset'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ position: 'fixed', top: 8, right: 8 }}>
        <ColorModeToggle />
      </Box>
      <Container maxWidth="xs">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 1 }}>
              <Text variant="display-small" as="h1" align="center">
                {t('forgotPin.title')}
              </Text>
            </Box>
            <Text variant="body-large" align="center" color="text.secondary">
              {t('forgotPin.subtitle')}
            </Text>
          </Box>

          <Stepper steps={steps} activeStep={step} />

          <Card>
            <CardContent>
              {step === 0 && (
                <form onSubmit={onSubmitPhone} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('forgotPin.phoneStep.prompt')}
                    </Typography>
                    <PhoneInput
                      value={phone}
                      onChangeText={setPhone}
                      error={fieldErrors.phone}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('forgotPin.sendingCode') : t('forgotPin.sendCode')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 1 && (
                <form onSubmit={onSubmitOtp} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('forgotPin.otpStep.prompt', { phone: `+27${phone}` })}
                    </Typography>
                    <Box>
                      <OtpInput
                        value={code}
                        onChange={setCode}
                        length={5}
                        error={!!fieldErrors.code}
                        disabled={pending}
                        aria-label={t('forgotPin.otpStep.ariaLabel')}
                      />
                      {fieldErrors.code && (
                        <Box sx={{ mt: 1 }}>
                          <Text variant="body-small" color="error.main">
                            {fieldErrors.code}
                          </Text>
                        </Box>
                      )}
                    </Box>

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('forgotPin.verifying') : t('forgotPin.verifyCode')}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={pending}
                      onClick={() => {
                        setApiErrorMessage(null);
                        setFieldErrors({});
                        setCode('');
                        setStep(0);
                      }}
                    >
                      {t('forgotPin.useDifferentNumber')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 2 && (
                <form onSubmit={onSubmitPin} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('forgotPin.pinStep.prompt')}
                    </Typography>
                    <TextField
                      label={t('forgotPin.newPinLabel')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      error={!!fieldErrors.pin}
                      helperText={fieldErrors.pin}
                      disabled={pending}
                    />
                    <TextField
                      label={t('forgotPin.confirmPinLabel')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value)}
                      error={!!fieldErrors.confirmPin}
                      helperText={fieldErrors.confirmPin}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('forgotPin.settingPin') : t('forgotPin.setNewPin')}
                    </Button>
                  </Stack>
                </form>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('forgotPin.backToSignIn')}
              </Typography>
            </Link>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
